/**
 * LocationCard.jsx - Shows the player's current location info
 */

import React, { useState } from 'react';
import { WONDERS } from '../../../data/wonders';
import { resources } from '../../../data/resources';

// Helper to get resource icon from canonical source
const getResourceIcon = (resourceId) => resources[resourceId]?.icon || '📦';

// Format effect values for display
const formatEffect = (value) => {
  if (value >= 1) return `+${Math.round(value * 100)}%`;
  return `+${Math.round(value * 100)}%`;
};

export function LocationCard({ currentNodeData, onViewKingdom, outpost }) {
  const [showDetails, setShowDetails] = useState(false);

  if (!currentNodeData) {
    return null;
  }

  // Check if this tile has an outpost
  const hasOutpost = !!outpost;
  const isOutpostConstructing = outpost?.isConstructing;

  // Check if this is a wonder site
  const isWonderSite = currentNodeData.biome === 'wonderSite' || currentNodeData.wonderSiteId;

  // Check if this is a kingdom biome
  const isKingdom = currentNodeData.biomeData?.isKingdomBiome;

  // Outpost tile rendering - takes priority over biome display
  // Uses standard layout like regular biomes but with outpost badge
  if (hasOutpost && !currentNodeData.isHub) {
    return (
      <div className="wm-location-card">
        <div className="wm-loc-icon">
          {currentNodeData.biomeData?.icon || '🏰'}
        </div>
        <div className="wm-loc-info">
          <h3 className="wm-outpost-title">{outpost.name}</h3>
          <p>{currentNodeData.biomeData?.name || 'Outpost'}</p>
        </div>
        <div className={`wm-outpost-badge ${isOutpostConstructing ? 'constructing' : ''}`}>
          {isOutpostConstructing ? '🔨 Building' : '🏰 Outpost'}
        </div>
      </div>
    );
  }

  if (isWonderSite) {
    const wonderId = currentNodeData.wonderSiteId;
    const wonder = wonderId ? WONDERS[wonderId] : null;

    return (
      <div
        className={`wm-location-card wm-wonder-site-card ${showDetails ? 'expanded' : ''}`}
        onClick={() => setShowDetails(!showDetails)}
        style={{ cursor: 'pointer' }}
      >
        <div className="wm-wonder-site-header">
          <span className="wm-wonder-site-icon">{currentNodeData.siteIcon || '🏛️'}</span>
          <div className="wm-wonder-site-title">
            <span className="wm-wonder-site-badge">Wonder Site</span>
            <h3>{currentNodeData.siteName || 'Ancient Wonder'}</h3>
          </div>
          <span className="wm-wonder-expand-hint">{showDetails ? '▲' : '▼'}</span>
        </div>

        {!showDetails ? (
          <div className="wm-wonder-site-body">
            <p className="wm-wonder-site-desc">
              You've discovered an ancient site! This legendary location unlocks the ability to build a powerful Wonder.
            </p>
            <div className="wm-wonder-site-cta">
              <span className="wm-wonder-cta-icon">📜</span>
              <span>Check the <strong>Wonders Panel</strong> to build!</span>
            </div>
            <div className="wm-wonder-click-hint">Click for details</div>
          </div>
        ) : wonder ? (
          <div className="wm-wonder-details">
            <p className="wm-wonder-lore">{wonder.lore}</p>

            <div className="wm-wonder-effects">
              <div className="wm-wonder-effects-label">Effects when built:</div>

              {wonder.effects.productionBonus && (
                <div className="wm-wonder-effect-group">
                  {Object.entries(wonder.effects.productionBonus).map(([res, val]) => (
                    <span key={res} className="wm-wonder-effect bonus">
                      {getResourceIcon(res)} {res}: {formatEffect(val)}
                    </span>
                  ))}
                </div>
              )}

              {wonder.effects.globalProductionBonus && (
                <div className="wm-wonder-effect-group">
                  <span className="wm-wonder-effect global">
                    🌍 All Production: {formatEffect(wonder.effects.globalProductionBonus)}
                  </span>
                </div>
              )}

              {wonder.effects.researchSpeedBonus && (
                <div className="wm-wonder-effect-group">
                  <span className="wm-wonder-effect research">
                    📖 Research Speed: {formatEffect(wonder.effects.researchSpeedBonus)}
                  </span>
                </div>
              )}

              {wonder.effects.flatBonus && (
                <div className="wm-wonder-effect-group">
                  {Object.entries(wonder.effects.flatBonus).map(([res, val]) => (
                    <span key={res} className="wm-wonder-effect flat">
                      {getResourceIcon(res)} +{val}/s {res}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="wm-wonder-tier">Tier {wonder.tier} Wonder</div>
          </div>
        ) : (
          <div className="wm-wonder-details">
            <p className="wm-wonder-lore">Ancient mysteries await...</p>
          </div>
        )}
      </div>
    );
  }

  // Kingdom tile rendering
  if (isKingdom) {
    const kingdomType = currentNodeData.biomeData.kingdomType;
    return (
      <div className="wm-location-card wm-kingdom-card">
        <div className="wm-loc-icon wm-kingdom-icon" style={{ backgroundColor: currentNodeData.biomeData.color }}>
          {currentNodeData.biomeData.icon}
        </div>
        <h3 style={{ textAlign: 'center', margin: '0.5rem 0' }}>{currentNodeData.biomeData.name}</h3>
        <div className="wm-kingdom-type" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          {kingdomType === 'kingdom' ? '👑 Kingdom' : '🏛️ City-State'} • Tier {currentNodeData.biomeData.kingdomTier}
        </div>
        <p style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{currentNodeData.biomeData.description}</p>
        <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--royal-blue)', fontStyle: 'italic' }}>
          💡 Open the Diplomacy tab to interact with this kingdom
        </div>
      </div>
    );
  }

  return (
    <div className="wm-location-card">
      <div className="wm-loc-icon">
        {currentNodeData.isHub ? '🏰' : currentNodeData.biomeData?.icon || '?'}
      </div>
      <div className="wm-loc-info">
        <h3>{currentNodeData.isHub ? 'Kingdom' : currentNodeData.biomeData?.name || 'Unknown'}</h3>
        <p>{currentNodeData.isHub ? 'Your home base' : currentNodeData.biomeData?.description || 'Unexplored'}</p>
      </div>
      {currentNodeData.hasOutpost && <div className="wm-outpost-badge">Outpost</div>}
    </div>
  );
}

export default LocationCard;
